import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { prettyChannelName } from "../../../channels/utils";
import { useFollowUpScheduleText } from "../../hooks/use-follow-up-schedule-text";
import {
  ANY_LINK_DYNAMIC_TOOLTIP,
  DynamicContentChip,
  SPECIFIC_LINK_DYNAMIC_TOOLTIP,
  SURVEY_DYNAMIC_TOOLTIP,
} from "../dynamic-content-chip";
import { FollowUpRecurringWarning } from "../follow-up-recurring-warning";

import type { FollowUpSchemaType } from "../../reducer/schema";
import type { FollowUpMomentDetails } from "../../server/types";
import type { FC } from "react";

interface DialogProps {
  reason: NonNullable<FollowUpSchemaType["reason"]>;
  schedule: FollowUpSchemaType["schedule"];
  channel: FollowUpSchemaType["channel"];
  moment: FollowUpMomentDetails;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
  dynamicOnlyLinks: Set<string>;
  dynamicOnlySurveyIds: Set<string>;
}

export const FollowUpPublishDialog: FC<DialogProps> = ({
  reason,
  schedule,
  channel,
  moment,
  open,
  onConfirm,
  onCancel,
  saving,
  dynamicOnlyLinks,
  dynamicOnlySurveyIds,
}) => {
  const reasonText = useMemo(() => {
    switch (reason.type) {
      case "no_interaction":
        return `Anyone who hasn’t opened or interacted with "${moment.title}"`;
      case "no_survey_completion":
        return (
          `Anyone who hasn’t completed the survey "${moment.surveys?.find((x) => x.id === reason.survey_id)?.title}"` +
          ` from the Communication "${moment.title}"`
        );
      case "link_click": {
        const target = reason.hyperlink_url
          ? `the link ${reason.hyperlink_url}`
          : `any link in "${moment.title}"`;
        return `Anyone who has clicked on ${target}`;
      }
      case "no_link_click": {
        const target = reason.hyperlink_url
          ? `the link ${reason.hyperlink_url}`
          : `any link in "${moment.title}"`;
        return `Anyone who hasn’t clicked on ${target}`;
      }
    }
  }, [reason, moment.title, moment.surveys]);

  const scheduleText = useFollowUpScheduleText(schedule, moment);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Publish Follow-Up?</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">Recipients:</Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {reasonText}
            </Typography>
            {(reason.type === "link_click" || reason.type === "no_link_click") && (
              <>
                {reason.hyperlink_url === null && dynamicOnlyLinks.size > 0 && (
                  <DynamicContentChip tooltipText={ANY_LINK_DYNAMIC_TOOLTIP} />
                )}
                {reason.hyperlink_url !== null && dynamicOnlyLinks.has(reason.hyperlink_url) && (
                  <DynamicContentChip tooltipText={SPECIFIC_LINK_DYNAMIC_TOOLTIP} />
                )}
              </>
            )}
            {reason.type === "no_survey_completion" &&
              dynamicOnlySurveyIds.has(reason.survey_id) && (
                <DynamicContentChip tooltipText={SURVEY_DYNAMIC_TOOLTIP} />
              )}
          </Stack>
          <FollowUpRecurringWarning details={moment} />
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">Channel:</Typography>
            <Stack direction="row" spacing={0.5}>
              <ChannelIcon channel={channel} fontSize="small" />
              <Typography variant="body2">{prettyChannelName(channel)}</Typography>
            </Stack>
          </Stack>
          <Typography variant="body2">{scheduleText}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          color="primary"
          data-analytics-id="follow-up-publish-cancel-button"
          variant="text"
          loading={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          data-analytics-id="follow-up-publish-confirm-button"
          variant="contained"
          loading={saving}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};
